import _ from 'lodash';
import React, { createContext, useCallback, useContext, useMemo, useRef, useState } from 'react';

import { RoleSettings } from '@/types/roleSettings';

/** What the card order menu needs to draw itself. */
export interface CardOrderSnapshot {
  /** Whether the reader may save a default for a role. */
  canEdit: boolean;
  /** The role whose default is on screen, or null when showing your own. */
  editingRole: string | null;
  hasPersonalOrder: boolean;
  /** Whether this page has anything to offer this reader at all. */
  isAvailable: boolean;
  isDirty: boolean;
  isSaving: boolean;
  /** The role whose layout the reader is on, or null if it matches none. */
  matchingRole: string | null;
  /** The roles the reader works in, which for most staff is just the one. */
  myRoles: RoleSettings[];
  /** Every role a default can be saved for. */
  roles: RoleSettings[];
}

export interface CardOrderHandlers {
  applyRoleOrder: (slug: string) => void;
  discardDraft: () => void;
  editRole: (slug: string | null) => void;
  resetToRoleDefault: () => void;
  saveDraft: () => Promise<void>;
}

export type CardOrderControls = CardOrderSnapshot & CardOrderHandlers;

export interface CardOrderContextInterface {
  /** Null until a page with sortable cards is on screen. */
  controls: CardOrderControls | null;
  /** Called by that page, every render, with null on the way out. */
  publish: (published: { snapshot: CardOrderSnapshot; handlers: CardOrderHandlers } | null) => void;
}

const NO_CONTROLS: CardOrderContextInterface = { controls: null, publish: () => {} };

const CardOrderContext = createContext<CardOrderContextInterface>(NO_CONTROLS);

/**
 * Connects a page's sortable cards to the card order menu in the toolbar.
 *
 * The menu belongs in the toolbar with the page's other controls, but the
 * order itself belongs to the page, and the two are rendered by different
 * parts of the tree. The page publishes what it has; the toolbar renders a
 * menu only while something is published.
 *
 * Handlers are held in a ref and called through stable wrappers: the page
 * republishes on every render, and storing its closures in state would mean a
 * render loop. Only the snapshot the menu draws from is state, and only when
 * it changes by value.
 */
export const CardOrderProvider = ({ children }: { children: React.ReactNode }) => {
  const handlersRef = useRef<CardOrderHandlers | null>(null);
  const [snapshot, setSnapshot] = useState<CardOrderSnapshot | null>(null);

  const publish = useCallback<CardOrderContextInterface['publish']>((published) => {
    handlersRef.current = published?.handlers ?? null;

    setSnapshot((current) =>
      _.isEqual(current, published?.snapshot ?? null) ? current : (published?.snapshot ?? null)
    );
  }, []);

  const handlers = useMemo<CardOrderHandlers>(
    () => ({
      applyRoleOrder: (slug) => handlersRef.current?.applyRoleOrder(slug),
      discardDraft: () => handlersRef.current?.discardDraft(),
      editRole: (slug) => handlersRef.current?.editRole(slug),
      resetToRoleDefault: () => handlersRef.current?.resetToRoleDefault(),
      saveDraft: async () => handlersRef.current?.saveDraft()
    }),
    []
  );

  const value = useMemo(
    () => ({ controls: snapshot && { ...snapshot, ...handlers }, publish }),
    [handlers, publish, snapshot]
  );

  return <CardOrderContext.Provider value={value}>{children}</CardOrderContext.Provider>;
};

/**
 * Outside a provider this reports no controls rather than throwing, so a page
 * or a toolbar can be rendered on its own.
 */
export const useCardOrder = () => useContext(CardOrderContext);

export default CardOrderContext;
